import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import classNames from 'classnames';
import get from 'lodash/get';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import Select, {
  components,
  GroupTypeBase,
  MenuListComponentProps,
  OptionProps,
  ValueType,
} from 'react-select';
import { SelectComponents } from 'react-select/src/components';

import ClearIndicator from './components/ClearIndicator';
import DropdownIndicator from './components/DropdownIndicator';
import MultiValueRemove from './components/MultiValueRemove';
import MenuList from './components/MenuList';
import MultiValue from './components/MultiValue';
import { MAX_SELECTED_TO_SHOW } from './components/MultiValue/MultiValue';
import Option from './components/Option';
import SelectAll from './components/SelectAll';
import customStylesAdditionally from './customStylesAdditionally';

import { IOption } from '../Select/types';
import isClickOnInteractiveElement from './optionSelectHelpers';

import customStyles from '../Select/customStyles';
import styles from '../Select/Select.module.scss';

type IValue = string | number;
type IsMulti = boolean;

const isMatchingOptionValue = (optionValue: IValue, selectedValue: IValue): boolean => {
  if (optionValue === selectedValue) return true;
  const a = Number(optionValue);
  const b = Number(selectedValue);
  return !Number.isNaN(a) && !Number.isNaN(b) && a === b;
};

interface IProps {
  control: Control<any>;
  selected?: IValue[];
  errors: FieldErrors;
  name: string;
  options: IOption[];
  label?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  maxMenuHeight?: number;
  minMenuHeight?: number;
  isMulti?: boolean;
  placeholder?: string;
  color?: string;
  disableMenu?: boolean;
  minHeight?: string;
  isRelativeWindow?: boolean;
  hideSelectedOptions?: boolean;
  openMenuOnFocus?: boolean;
  showCustomComponents?: boolean;
  closeMenuOnSelect?: boolean;
  showSelectAll?: boolean;
  oneLineMulti?: boolean;
  isNarrow?: boolean;
  tabIndex?: number;
  customComponents?: Partial<SelectComponents<IOption, boolean, GroupTypeBase<IOption>>>;
  CustomOption?: (
    optionProps: OptionProps<IOption, boolean, GroupTypeBase<IOption>>,
  ) => ReactElement;
  additionalStyleHandler?: () => any;
  setAll?: (all?: boolean) => void;
  onInputChange?: (value: string) => void;
  onSelect?: (value: ValueType<IOption | IOption[], IsMulti>) => void;
  isOnMountSelectAll?: boolean;
  isOptionDisabled?: (option: IOption) => boolean;
  /** Hides remove (×) button on selected multi-value chips in the input */
  hideMultiValueRemove?: boolean;
  /** Runs after multi-select value is parsed (clear, chip remove, menu select). Use to enforce invariants. */
  normalizeMultiValue?: (value: IValue[]) => IValue[];
  menuPortalTarget?: HTMLElement | null;
}

const SelectComponent = ({
  control,
  selected,
  errors,
  name,
  options: allOptions,
  label,
  isDisabled = false,
  disableMenu,
  isLoading = false,
  isMulti = false,
  isSearchable = true,
  isClearable = false,
  maxMenuHeight = 200,
  minMenuHeight,
  placeholder = '',
  color = 'black',
  isOnMountSelectAll = true,
  minHeight,
  tabIndex,
  isRelativeWindow = true,
  hideSelectedOptions = true,
  openMenuOnFocus = false,
  showCustomComponents = false,
  closeMenuOnSelect = true,
  showSelectAll = false,
  oneLineMulti = false,
  isNarrow = false,
  customComponents,
  CustomOption,
  additionalStyleHandler = () => {},
  setAll,
  onInputChange,
  onSelect,
  isOptionDisabled,
  hideMultiValueRemove = false,
  normalizeMultiValue,
  menuPortalTarget = document.body,
}: IProps): ReactElement => {
  // cut multi select options up to MAX_SELECTED, in order to show only MAX_SELECTED items
  const getFilteredOptions = () => {
    const selectedOptions = { ...selected };
    const hiddenOptions =
      selectedOptions.length > MAX_SELECTED_TO_SHOW
        ? selectedOptions.slice(0, MAX_SELECTED_TO_SHOW)
        : [];
    return allOptions.filter(x => !hiddenOptions.includes(Number(x.value)));
  };

  const options = isMulti && oneLineMulti ? getFilteredOptions() : allOptions;

  // eslint-disable-next-line no-underscore-dangle
  const selectedValues = control._formValues?.[name]?.length;

  const isAllSelected = useMemo(
    () => allOptions?.length && allOptions?.length === selectedValues,
    [allOptions?.length, selectedValues],
  );

  const [selectAll, setSelectAll] = useState(false);
  const error = get(errors, `${name}.message`, '');
  const isError = Boolean(error);

  const formatValue = (option: ValueType<IOption | IOption[], IsMulti>) => {
    const value = isMulti
      ? (option as IOption[]).map((item: IOption) => item.value)
      : (option as IOption)?.value ?? '';
    return value;
  };

  const getValue = (value: IValue | IValue[]): IOption | IOption[] => {
    if (options) {
      if (isMulti && Array.isArray(value)) {
        return options.filter(option =>
          (value as IValue[]).some(selectedVal => isMatchingOptionValue(option.value, selectedVal)),
        );
      }
      const raw = value as IValue;
      return options.find(option => isMatchingOptionValue(option.value, raw)) ?? ('' as any);
    }

    return isMulti ? [] : ('' as any);
  };

  const handleChange = (option: ValueType<IOption | IOption[], IsMulti>) => {
    if (Array.isArray(option) && showSelectAll) {
      setSelectAll(allOptions.length > 0 && option.length === allOptions.length);
    }
    onSelect?.(option);
    return formatValue(option);
  };

  const mergedStyles = {
    ...customStyles,
    ...customStylesAdditionally(
      options,
      maxMenuHeight,
      error as string,
      minHeight,
      isRelativeWindow,
      color,
      showSelectAll,
      isNarrow,
      isAllSelected,
      isDisabled,
      showCustomComponents && !showSelectAll,
    ),
    ...additionalStyleHandler(),
  };
  const labelStyles = classNames(styles.label, { [styles.labelError]: error });

  const OptionWithSelectOnMouseDown = useCallback(
    (props: OptionProps<IOption, boolean, GroupTypeBase<IOption>>) => {
      const { selectOption, data, innerProps } = props;
      const optionDisabled = props.isDisabled;
      const OptionComponent = CustomOption || (showCustomComponents ? Option : components.Option);
      const enhancedInnerProps = {
        ...innerProps,
        onMouseDown: (e: React.MouseEvent<HTMLElement>) => {
          const interactive = isClickOnInteractiveElement(e.target);
          if (interactive) e.preventDefault();
          if (!optionDisabled && !interactive) selectOption(data);
          (
            innerProps as { onMouseDown?: (e: React.MouseEvent<HTMLElement>) => void }
          ).onMouseDown?.(e);
        },
        onClick: (e: React.MouseEvent<HTMLElement>) => {
          if (!isClickOnInteractiveElement(e.target)) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
      };
      return <OptionComponent {...props} innerProps={enhancedInnerProps} />;
    },
    [CustomOption, showCustomComponents],
  );

  // selecting custom dropdown header component
  const MenuHeader = useMemo(
    () =>
      showSelectAll
        ? (optionProps: MenuListComponentProps<IOption, boolean, GroupTypeBase<IOption>>) => (
            <SelectAll
              {...optionProps}
              selectAll={selectAll}
              setSelectAll={setSelectAll}
              onSelectAllChange={all => setAll?.(all)}
            />
          )
        : MenuList,
    [showSelectAll, selectAll, setAll],
  );

  useEffect(() => {
    if (showSelectAll && isAllSelected && isOnMountSelectAll) {
      setSelectAll(true);
    }
  }, [isAllSelected, isOnMountSelectAll, showSelectAll]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange, onBlur } }) => (
        <FormControl component="fieldset" className={styles.selectControl}>
          <FormLabel component="legend" className={labelStyles} error={isError}>
            {label}
          </FormLabel>
          <Select
            tabSelectsValue={false}
            instanceId={name}
            tabIndex={String(tabIndex)}
            styles={mergedStyles}
            isDisabled={isDisabled}
            closeMenuOnSelect={closeMenuOnSelect}
            isLoading={isLoading}
            openMenuOnFocus={openMenuOnFocus}
            isSearchable={isSearchable}
            hideSelectedOptions={hideSelectedOptions}
            maxMenuHeight={maxMenuHeight}
            minMenuHeight={minMenuHeight}
            name={name}
            components={{
              Option: OptionWithSelectOnMouseDown,
              DropdownIndicator,
              ClearIndicator,
              MultiValueRemove: hideMultiValueRemove ? (): null => null : MultiValueRemove,
              MenuList: showCustomComponents ? MenuHeader : components.MenuList,
              MultiValue: isMulti && oneLineMulti ? MultiValue : components.MultiValue,
              ...(disableMenu
                ? {
                    Menu: function hideMenu() {
                      return <></>;
                    },
                  }
                : {}),

              ...(customComponents || {}),
            }}
            menuShouldScrollIntoView
            options={options}
            placeholder={placeholder}
            isMulti={isMulti}
            value={getValue(value)}
            onChange={val => {
              const parsed = handleChange(val);
              if (isMulti && normalizeMultiValue && Array.isArray(parsed)) {
                onChange(normalizeMultiValue(parsed as IValue[]));
                return;
              }
              onChange(parsed);
            }}
            onInputChange={onInputChange}
            onBlur={onBlur}
            menuPortalTarget={menuPortalTarget}
            isClearable={isClearable}
            isOptionDisabled={isOptionDisabled}
          />
          {error && (
            <FormHelperText error className={styles.error}>
              {error.toString()}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};

export default SelectComponent;
